const STOREFRONT_API_BASE = "https://storefront-api.fourthwall.com/v1";
const DEFAULT_COLLECTION_SLUG = "all";
export const DEFAULT_FOURTHWALL_CURRENCY = "USD";
const DEFAULT_SHOP_DOMAIN = "simpleble.store";
const FOURTHWALL_UNCONFIGURED_MESSAGE =
  "FOURTHWALL_STOREFRONT_TOKEN is not configured.";

// How long (seconds) Next.js caches storefront GETs before revalidating. Lower =
// product/catalog edits surface faster on the live site, at the cost of more
// frequent refetches. Cart mutations bypass this (they use cache: "no-store").
const STOREFRONT_REVALIDATE_SECONDS = 60;

type FourthwallState = Readonly<{
  type: string;
}>;

type FourthwallAccess = Readonly<{
  type: string;
}>;

type FourthwallMoney = Readonly<{
  value: number;
  currency: string;
}>;

export type FourthwallCollection = Readonly<{
  id: string;
  name: string;
  slug: string;
  description?: string;
}>;

export type FourthwallProductOption = Readonly<{
  name: string;
  swatch?: string;
}>;

export type FourthwallPurchaseVariant = Readonly<{
  id: string;
  colorName?: string;
  sizeName?: string;
  swatchColor: string;
  priceLabel: string | null;
  compareAtPriceLabel: string | null;
  isAvailable: boolean;
  priceValue: number;
  currency: string;
}>;

export type FourthwallImage = Readonly<{
  id: string;
  url: string;
  width: number;
  height: number;
  transformedUrl?: string;
}>;

export type FourthwallVariant = Readonly<{
  id: string;
  name: string;
  sku?: string;
  unitPrice?: FourthwallMoney;
  compareAtPrice?: FourthwallMoney;
  attributes?: Readonly<{
    description?: string;
    color?: Readonly<{
      name: string;
      swatch?: string;
    }>;
    size?: Readonly<{
      name: string;
    }>;
  }>;
  stock?: Readonly<{
    type: string;
    inStock?: number;
  }>;
  images?: readonly FourthwallImage[];
}>;

export type FourthwallCartItemInput = Readonly<{
  variantId: string;
  quantity: number;
  recStrategy?: string;
}>;

export type FourthwallCartItem = Readonly<{
  variant: FourthwallVariant &
    Readonly<{
      product?: Pick<FourthwallProduct, "id" | "name" | "slug">;
    }>;
  quantity: number;
  groupedBy?: Readonly<Record<string, string>>;
}>;

export type FourthwallCart = Readonly<{
  id: string;
  items: readonly FourthwallCartItem[];
  metadata: Readonly<Record<string, string>>;
}>;

export type FourthwallCartResult = Readonly<{
  live: boolean;
  cart: FourthwallCart;
  checkoutUrl: string;
  message?: string;
}>;

export type FourthwallAdditionalInfo = Readonly<{
  type: string;
  title: string;
  bodyHtml: string;
}>;

export type FourthwallProductDetailSection = Readonly<{
  title: string;
  items: readonly string[];
}>;

export type FourthwallSizeGuideSource = Readonly<{
  previewUrl?: string | null;
  fileUrl?: string | null;
  description?: string | null;
}>;

export type FourthwallSizeGuideMeasurement = Readonly<{
  label: string;
  description: string;
}>;

export type FourthwallSizeGuide = Readonly<{
  imageUrl: string | null;
  notes: readonly string[];
  measurements: readonly FourthwallSizeGuideMeasurement[];
}>;

export type FourthwallProduct = Readonly<{
  id: string;
  name: string;
  slug: string;
  description: string;
  state: FourthwallState;
  access: FourthwallAccess;
  images: readonly FourthwallImage[];
  variants: readonly FourthwallVariant[];
  additionalInformation?: readonly FourthwallAdditionalInfo[];
  sizeGuide?: FourthwallSizeGuideSource | null;
  createdAt: string;
  updatedAt: string;
}>;

export type FourthwallShop = Readonly<{
  id: string;
  name: string;
  domain: string;
  publicDomain: string;
}>;

type PageResult<T> = Readonly<{
  results: readonly T[];
  paging: Readonly<{
    pageNumber: number;
    pageSize: number;
    elementsSize: number;
    elementsTotal: number;
    totalPages: number;
    hasNextPage: boolean;
  }>;
}>;

export class FourthwallApiError extends Error {
  readonly status: number;

  constructor(status: number, message: string) {
    super(message);
    this.name = "FourthwallApiError";
    this.status = status;
  }
}

const getStorefrontToken = (): string | undefined => {
  return process.env.FOURTHWALL_STOREFRONT_TOKEN;
};

export const hasFourthwallStorefrontToken = (): boolean => {
  return Boolean(getStorefrontToken());
};

export const getFourthwallShopDomain = (): string => {
  return (
    process.env.NEXT_PUBLIC_FOURTHWALL_SHOP_DOMAIN ??
    process.env.FOURTHWALL_SHOP_DOMAIN ??
    DEFAULT_SHOP_DOMAIN
  );
};

const buildStorefrontUrl = (
  path: string,
  params: Readonly<Record<string, string | number | undefined>> = {},
): string | null => {
  const token = getStorefrontToken();

  if (!token) {
    return null;
  }

  const url = new URL(`${STOREFRONT_API_BASE}${path}`);
  url.searchParams.set("storefront_token", token);

  Object.entries(params).forEach(([key, value]) => {
    if (value !== undefined) {
      url.searchParams.set(key, String(value));
    }
  });

  return url.toString();
};

const fetchStorefront = async <T>(
  path: string,
  params?: Readonly<Record<string, string | number | undefined>>,
  init?: RequestInit,
): Promise<T | null> => {
  const url = buildStorefrontUrl(path, params);

  if (!url) {
    return null;
  }

  const response = await fetch(url, init ?? {
    next: { revalidate: STOREFRONT_REVALIDATE_SECONDS },
  });

  if (!response.ok) {
    return null;
  }

  return (await response.json()) as T;
};

const mutateStorefront = async <T>(
  path: string,
  body: unknown,
  params?: Readonly<Record<string, string | number | undefined>>,
): Promise<T> => {
  const url = buildStorefrontUrl(path, params);

  if (!url) {
    throw new FourthwallApiError(503, FOURTHWALL_UNCONFIGURED_MESSAGE);
  }

  const response = await fetch(url, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(body),
    cache: "no-store",
  });

  if (!response.ok) {
    throw new FourthwallApiError(
      response.status,
      `Fourthwall request failed with status ${response.status}.`,
    );
  }

  return (await response.json()) as T;
};

export const getFourthwallShop = async (): Promise<FourthwallShop | null> => {
  return fetchStorefront<FourthwallShop>("/shop");
};

export const getFourthwallProducts = async (
  collectionSlug = DEFAULT_COLLECTION_SLUG,
): Promise<readonly FourthwallProduct[]> => {
  if (!hasFourthwallStorefrontToken()) {
    return [];
  }

  const data = await fetchStorefront<PageResult<FourthwallProduct>>(
    `/collections/${collectionSlug}/products`,
    {
      currency: DEFAULT_FOURTHWALL_CURRENCY,
      size: 50,
    },
  );

  return data?.results ?? [];
};

export const getFourthwallCollections = async (): Promise<
  readonly FourthwallCollection[]
> => {
  if (!hasFourthwallStorefrontToken()) {
    return [];
  }

  const data = await fetchStorefront<PageResult<FourthwallCollection>>(
    "/collections",
    { size: 50 },
  );

  return data?.results ?? [];
};

export const getFourthwallProductBySlug = async (
  slug: string,
): Promise<FourthwallProduct | null> => {
  if (!hasFourthwallStorefrontToken()) {
    return null;
  }

  return fetchStorefront<FourthwallProduct>(`/products/${slug}`, {
    currency: DEFAULT_FOURTHWALL_CURRENCY,
  });
};

export const getFourthwallProductImage = (
  product: FourthwallProduct,
): FourthwallImage | undefined => {
  return product.images[0] ?? product.variants[0]?.images?.[0];
};

export const getFourthwallProductImages = (
  product: FourthwallProduct,
  limit = 4,
): readonly FourthwallImage[] => {
  const images: FourthwallImage[] = [];
  const seenUrls = new Set<string>();
  const candidates = [
    ...product.images,
    ...product.variants.flatMap((variant) => variant.images ?? []),
  ];

  candidates.forEach((image) => {
    if (images.length >= limit || seenUrls.has(image.url)) {
      return;
    }

    seenUrls.add(image.url);
    images.push(image);
  });

  return images;
};

export const getFourthwallImagesByColor = (
  product: FourthwallProduct,
  limit = 8,
): Readonly<Record<string, readonly FourthwallImage[]>> => {
  const byColor: Record<string, FourthwallImage[]> = {};

  product.variants.forEach((variant) => {
    const colorName = variant.attributes?.color?.name;

    if (!colorName) {
      return;
    }

    const list = (byColor[colorName] ??= []);
    const seenUrls = new Set(list.map((image) => image.url));

    (variant.images ?? []).forEach((image) => {
      if (list.length >= limit || seenUrls.has(image.url)) {
        return;
      }

      seenUrls.add(image.url);
      list.push(image);
    });
  });

  return byColor;
};

export const getFourthwallProductUrl = (product: FourthwallProduct): string => {
  const domain = getFourthwallShopDomain();

  return `https://${domain}/products/${product.slug}`;
};

export const getFourthwallStoreUrl = (shop?: FourthwallShop | null): string => {
  const domain = shop?.publicDomain || getFourthwallShopDomain();

  return `https://${domain}`;
};

export const getFourthwallCheckoutDomain = (): string => {
  return (
    process.env.NEXT_PUBLIC_FOURTHWALL_CHECKOUT_DOMAIN ??
    process.env.FOURTHWALL_CHECKOUT_DOMAIN ??
    "checkout.simpleble.store"
  );
};

export const getFourthwallCheckoutUrl = (
  variantId: string,
  quantity = 1,
  currency = DEFAULT_FOURTHWALL_CURRENCY,
): string => {
  const domain = getFourthwallCheckoutDomain();
  const params = new URLSearchParams({
    products: `${variantId}:${quantity}`,
    currency,
  });

  return `https://${domain}/cart/checkout?${params.toString()}`;
};

export const getFourthwallCartCheckoutUrl = (
  cartId: string,
  currency = DEFAULT_FOURTHWALL_CURRENCY,
): string => {
  const domain = getFourthwallCheckoutDomain();
  const params = new URLSearchParams({ cartId, currency });

  return `https://${domain}/cart/checkout?${params.toString()}`;
};

const requireFourthwallStorefrontToken = (): void => {
  if (!hasFourthwallStorefrontToken()) {
    throw new FourthwallApiError(503, FOURTHWALL_UNCONFIGURED_MESSAGE);
  }
};

const buildCartResult = (cart: FourthwallCart): FourthwallCartResult => {
  return {
    live: true,
    cart,
    checkoutUrl: getFourthwallCartCheckoutUrl(cart.id),
  };
};

export const createFourthwallCart = async (
  items: readonly FourthwallCartItemInput[],
  metadata: Readonly<Record<string, string>> = {},
): Promise<FourthwallCartResult> => {
  requireFourthwallStorefrontToken();

  const cart = await mutateStorefront<FourthwallCart>(
    "/carts",
    { items, metadata },
    { currency: DEFAULT_FOURTHWALL_CURRENCY },
  );

  return buildCartResult(cart);
};

export const getFourthwallCart = async (
  cartId: string,
): Promise<FourthwallCartResult> => {
  requireFourthwallStorefrontToken();

  const cart = await fetchStorefront<FourthwallCart>(`/carts/${cartId}`, {
    currency: DEFAULT_FOURTHWALL_CURRENCY,
  });

  if (!cart) {
    throw new FourthwallApiError(404, "Fourthwall cart was not found.");
  }

  return buildCartResult(cart);
};

export const addFourthwallCartItems = async (
  cartId: string,
  items: readonly FourthwallCartItemInput[],
  metadata: Readonly<Record<string, string>> = {},
): Promise<FourthwallCartResult> => {
  requireFourthwallStorefrontToken();

  const cart = await mutateStorefront<FourthwallCart>(
    `/carts/${cartId}/add`,
    { items, metadata },
    { currency: DEFAULT_FOURTHWALL_CURRENCY },
  );

  return buildCartResult(cart);
};

export const changeFourthwallCartItems = async (
  cartId: string,
  items: readonly FourthwallCartItemInput[],
  metadata: Readonly<Record<string, string>> = {},
): Promise<FourthwallCartResult> => {
  requireFourthwallStorefrontToken();

  const cart = await mutateStorefront<FourthwallCart>(
    `/carts/${cartId}/change`,
    { items, metadata },
    { currency: DEFAULT_FOURTHWALL_CURRENCY },
  );

  return buildCartResult(cart);
};

export const removeFourthwallCartItems = async (
  cartId: string,
  items: readonly FourthwallCartItemInput[],
  metadata: Readonly<Record<string, string>> = {},
): Promise<FourthwallCartResult> => {
  requireFourthwallStorefrontToken();

  const cart = await mutateStorefront<FourthwallCart>(
    `/carts/${cartId}/remove`,
    { items, metadata },
    { currency: DEFAULT_FOURTHWALL_CURRENCY },
  );

  return buildCartResult(cart);
};

export const formatFourthwallMoney = (money: FourthwallMoney): string => {
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: money.currency,
    maximumFractionDigits: money.value % 1 === 0 ? 0 : 2,
  }).format(money.value);
};

export const getFourthwallPriceLabel = (
  product: FourthwallProduct,
): string | null => {
  const prices = product.variants
    .map((variant) => variant.unitPrice)
    .filter((price): price is FourthwallMoney => Boolean(price));

  if (prices.length === 0) {
    return null;
  }

  const [firstPrice] = prices;
  const minPrice = prices.reduce((lowest, price) => {
    return price.value < lowest.value ? price : lowest;
  }, firstPrice);

  const hasMultiplePrices = prices.some((price) => {
    return (
      price.value !== minPrice.value || price.currency !== minPrice.currency
    );
  });

  const formatted = formatFourthwallMoney(minPrice);

  return hasMultiplePrices ? `From ${formatted}` : formatted;
};

export const getFourthwallLowestVariantPrice = (
  product: FourthwallProduct,
): FourthwallMoney | null => {
  const prices = product.variants
    .map((variant) => variant.unitPrice)
    .filter((price): price is FourthwallMoney => Boolean(price));

  if (prices.length === 0) {
    return null;
  }

  const [firstPrice] = prices;

  return prices.reduce((lowest, price) => {
    return price.value < lowest.value ? price : lowest;
  }, firstPrice);
};

const decodeHtmlEntities = (value: string): string => {
  return value
    .replace(/&nbsp;/gi, " ")
    .replace(/&amp;/gi, "&")
    .replace(/&lt;/gi, "<")
    .replace(/&gt;/gi, ">")
    .replace(/&quot;/gi, '"')
    .replace(/&#0*39;|&apos;/gi, "'")
    .replace(/&#(\d+);/g, (_, code) => String.fromCharCode(Number(code)));
};

const textFromHtml = (value: string): string => {
  return decodeHtmlEntities(
    value
      .replace(/<script[\s\S]*?<\/script>/gi, " ")
      .replace(/<style[\s\S]*?<\/style>/gi, " ")
      .replace(/<[^>]+>/g, " "),
  )
    .replace(/\s+/g, " ")
    .trim();
};

export const getFourthwallProductDescription = (
  product: FourthwallProduct,
): string => {
  return textFromHtml(product.description);
};

const listItemsFromHtml = (html: string): string[] => {
  const items = [...html.matchAll(/<li[^>]*>([\s\S]*?)<\/li>/gi)]
    .map((match) => textFromHtml(match[1]))
    .filter(Boolean);

  if (items.length > 0) {
    return items;
  }

  const text = textFromHtml(html);

  return text ? [text] : [];
};

export const getFourthwallProductDetails = (
  product: FourthwallProduct,
): readonly FourthwallProductDetailSection[] => {
  return (product.additionalInformation ?? [])
    .map((info) => ({
      title: info.title,
      items: listItemsFromHtml(info.bodyHtml),
    }))
    .filter((section) => section.items.length > 0);
};

export const getFourthwallSizeGuide = (
  product: FourthwallProduct,
): FourthwallSizeGuide | null => {
  const source = product.sizeGuide;

  if (!source) {
    return null;
  }

  const html = source.description ?? "";

  // Leading paragraphs (before the first measurement heading) are general notes.
  const beforeFirstHeading = html.split(/<h[1-6][^>]*>/i)[0] ?? "";
  const notes = [...beforeFirstHeading.matchAll(/<p[^>]*>([\s\S]*?)<\/p>/gi)]
    .map((match) => textFromHtml(match[1]))
    .filter(Boolean);

  // Each heading plus the content that follows it is one labelled measurement.
  const measurements: FourthwallSizeGuideMeasurement[] = [];
  const sectionRegex =
    /<h[1-6][^>]*>([\s\S]*?)<\/h[1-6]>([\s\S]*?)(?=<h[1-6][^>]*>|$)/gi;
  let match: RegExpExecArray | null;

  while ((match = sectionRegex.exec(html)) !== null) {
    const label = textFromHtml(match[1]);
    const description = textFromHtml(match[2]);

    if (label) {
      measurements.push({ label, description });
    }
  }

  const imageUrl = source.previewUrl || source.fileUrl || null;

  if (!imageUrl && notes.length === 0 && measurements.length === 0) {
    return null;
  }

  return { imageUrl, notes, measurements };
};

export const getFourthwallProductColors = (
  product: FourthwallProduct,
): readonly FourthwallProductOption[] => {
  const colors: FourthwallProductOption[] = [];
  const seenNames = new Set<string>();

  product.variants.forEach((variant) => {
    const color = variant.attributes?.color;

    if (!color || seenNames.has(color.name)) {
      return;
    }

    seenNames.add(color.name);
    colors.push({ name: color.name, swatch: color.swatch });
  });

  return colors;
};

export const getFourthwallProductSizes = (
  product: FourthwallProduct,
): readonly string[] => {
  const sizes: string[] = [];
  const seenNames = new Set<string>();

  product.variants.forEach((variant) => {
    const size = variant.attributes?.size?.name;

    if (!size || seenNames.has(size)) {
      return;
    }

    seenNames.add(size);
    sizes.push(size);
  });

  return sizes;
};

const DEFAULT_SWATCH_COLOR = "#d4d4d8";

export const getFourthwallPurchaseVariants = (
  product: FourthwallProduct,
): readonly FourthwallPurchaseVariant[] => {
  return product.variants.map((variant) => {
    const color = variant.attributes?.color;

    return {
      id: variant.id,
      colorName: color?.name,
      sizeName: variant.attributes?.size?.name,
      swatchColor: color?.swatch ?? DEFAULT_SWATCH_COLOR,
      priceLabel: variant.unitPrice
        ? formatFourthwallMoney(variant.unitPrice)
        : null,
      // Only a compare-at above the selling price is a real "was" price.
      compareAtPriceLabel:
        variant.compareAtPrice &&
        variant.unitPrice &&
        variant.compareAtPrice.value > variant.unitPrice.value
          ? formatFourthwallMoney(variant.compareAtPrice)
          : null,
      isAvailable: variant.stock?.inStock !== 0,
      priceValue: variant.unitPrice?.value ?? 0,
      currency: variant.unitPrice?.currency ?? DEFAULT_FOURTHWALL_CURRENCY,
    };
  });
};

export const isFourthwallProductSoldOut = (
  product: FourthwallProduct,
): boolean => {
  if (product.state.type.toLowerCase().includes("sold")) {
    return true;
  }

  return (
    product.variants.length > 0 &&
    product.variants.every((variant) => variant.stock?.inStock === 0)
  );
};
